import { useNavigate } from "react-router";
import { LogOut, Menu, Moon, Sun, User } from "lucide-react";
import { AccountSwitcher } from "@/account/switcher";
import { useExpiryWarning, useSession, useTokenTtl } from "@/auth/session";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MyStatus } from "@/domains/pbx/call-center/my-status";
import { useSocketState } from "@/realtime/socket";
import { cn } from "@/lib/utils";

const SOCKET_LABEL: Record<string, string> = {
  idle: "socket idle",
  connecting: "socket connecting",
  open: "live",
  reconnecting: "socket reconnecting",
  closed: "socket closed",
};

export function TopBar({ onOpenColumn, showAccount }: { onOpenColumn?: () => void; showAccount: boolean }) {
  const session = useSession((s) => s.session);
  const logout = useSession((s) => s.logout);
  const { resolved, toggle } = useTheme();
  const socketState = useSocketState((s) => s.state);
  const ttl = useTokenTtl();
  const expiring = useExpiryWarning();
  const navigate = useNavigate();

  return (
    <header className="flex min-h-10 min-w-0 shrink-0 flex-wrap items-center gap-2 bg-panel px-2 hairline-b">
      <Button variant="ghost" size="icon" className="size-7 lg:hidden" onClick={onOpenColumn} aria-label="Open sections">
        <Menu className="size-4" />
      </Button>
      <a href="/" className="flex items-center gap-1.5 pr-1 text-sm font-semibold tracking-tight" aria-label="Switchboard home">
        <span className="size-4 rounded-[5px] bg-[linear-gradient(135deg,#83B437_0%,#E9973E_58%,#C7325D_100%)]" aria-hidden="true" />
        <span className="hidden sm:inline">Switchboard</span>
      </a>
      {showAccount && <AccountSwitcher className="min-w-0 max-w-[150px] sm:max-w-[260px]" />}
      <div className="flex-1" />
      {/* The signed-in user's own call center availability, when they are an agent. */}
      <MyStatus />
      {expiring && ttl !== undefined && (
        <span className="text-xs text-warn" title="This account does not allow token renewal; sign in again before it lapses">
          session ends in {Math.ceil(ttl / 60)} min
        </span>
      )}
      <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex" title={SOCKET_LABEL[socketState]}>
        <span
          className={cn(
            "size-1.5 rounded-full",
            socketState === "open" ? "bg-ok shadow-[0_0_0_2px_hsl(var(--ok)/0.25)]" : socketState === "reconnecting" ? "bg-warn" : "bg-border",
          )}
          aria-hidden="true"
        />
        {SOCKET_LABEL[socketState]}
      </span>
      <Button variant="ghost" size="icon" className="size-7" onClick={toggle} aria-label={resolved === "dark" ? "Switch to light" : "Switch to dark"}>
        {resolved === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7" aria-label="Account menu">
            <User className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-medium">{session?.accountName ?? "Signed in"}</div>
            <div className="truncate font-mono text-2xs text-muted-foreground">{session?.accountId}</div>
            {session?.isReseller && <div className="text-2xs text-muted-foreground">reseller</div>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(`/a/${session?.accountId}/pbx/users`)}>Home account</DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <LogOut className="size-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
